import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export type GateState = 'done' | 'current' | 'open' | 'blocked';

export interface Gate {
  num: number | string;
  label: React.ReactNode;
  sub?: React.ReactNode;
  state: GateState;
}

export interface GateStripProps extends React.HTMLAttributes<HTMLDivElement> {
  gates: Gate[];
  orientation?: 'horizontal' | 'vertical';
}

/**
 * GateStrip — the evidence-gate ladder used by Acquisition OS workspaces.
 * Horizontal default for engine workspaces; vertical for sidebars.
 */
export const GateStrip = React.forwardRef<HTMLDivElement, GateStripProps>(
  ({ gates, orientation = 'horizontal', className, ...rest }, ref) => {
    const isVertical = orientation === 'vertical';
    return (
      <div
        ref={ref}
        className={cn(
          'gap-2',
          isVertical ? 'grid grid-cols-1' : `grid`,
          !isVertical && `grid-cols-${gates.length}`,
          className,
        )}
        style={!isVertical ? { gridTemplateColumns: `repeat(${gates.length}, 1fr)` } : undefined}
        {...rest}
      >
        {gates.map((g, i) => (
          <GateCell key={i} gate={g} />
        ))}
      </div>
    );
  },
);
GateStrip.displayName = 'GateStrip';

function GateCell({ gate }: { gate: Gate }) {
  const tone = {
    done: 'border-fd-border bg-fd-surface-2',
    current:
      'border-fd-accent-line bg-gradient-to-b from-fd-accent-soft to-fd-surface-2',
    open: 'border-fd-border bg-fd-surface-2',
    blocked: 'border-fd-bad/30 bg-fd-bad/10',
  }[gate.state];

  const dot = {
    done: 'bg-fd-ok',
    current: 'bg-fd-accent shadow-[0_0_0_3px_var(--fd-accent-soft)]',
    open: 'bg-fd-text-subtle',
    blocked: 'bg-fd-bad',
  }[gate.state];

  return (
    <div
      className={cn(
        'relative flex flex-col gap-1.5 rounded-fd p-3 min-h-[88px] border',
        tone,
      )}
    >
      <span className={cn('absolute right-3 top-3 h-1.5 w-1.5 rounded-full', dot)} />
      <span className="font-geist-mono text-[10px] uppercase tracking-[0.06em] text-fd-text-subtle font-medium">
        {gate.state === 'done' ? (
          <span className="inline-flex items-center gap-1">
            <Check className="h-3 w-3 text-fd-ok" /> {gate.num}
          </span>
        ) : (
          <>Gate {gate.num}</>
        )}
      </span>
      <span className="font-geist text-[12.5px] font-medium leading-tight text-fd-text">
        {gate.label}
      </span>
      {gate.sub && (
        <span className="font-geist-mono text-[10px] uppercase tracking-[0.04em] text-fd-text-subtle">
          {gate.sub}
        </span>
      )}
    </div>
  );
}
